<script module lang="ts">
  /**
   * Which of the Modifier base class' optional lifecycle hooks an effect
   * implements is the clearest statement of *when* it actually does something,
   * and it cannot drift from the code the way a hand-written note would.
   *
   * Several hooks collapse to one phrase -- an effect that touches rollStart and
   * rollMid still just acts while the dice roll -- so the phrases are deduped in
   * order below.
   */
  const HOOK_TIMING: Array<[string, string]> = [
    ['beforeStartTurn', 'At the start of the turn'],
    ['startTurn', 'At the start of the turn'],
    ['actionPhaseStart', 'When the action phase begins'],
    ['rollStart', 'While the dice roll'],
    ['rollMid', 'While the dice roll'],
    ['rollEnd', 'While the dice roll'],
    ['getDice', 'Changes the dice rolled'],
    ['getTechniques', 'Changes the available techniques'],
    ['canUseTechnique', 'Restricts which techniques can be used'],
    ['canInterrupt', 'Restricts interrupts'],
    ['useTechnique', 'When a technique is used'],
    ['damageOut', 'When damage is dealt'],
    ['damageIn', 'When damage is taken'],
    ['healingIn', 'When healing is received'],
    ['modifierAdd', 'When another effect is applied'],
    ['checkDied', 'When health reaches zero'],
    ['endTurn', 'At the end of the turn'],
  ];

  export function describeTiming(source: Record<string, unknown> | undefined) {
    if (!source) {
      return [];
    }

    const timings: Array<string> = [];
    HOOK_TIMING.forEach(([hook, phrase]) => {
      if (typeof source[hook] === 'function' && !timings.includes(phrase)) {
        timings.push(phrase);
      }
    });
    return timings;
  }
</script>

<script lang="ts">
  import Icon from '../../icon/icon.svelte';
  import { ModifierTags } from '../../models/modifier.type';
  import Text from '../../components/text.svelte';

  /**
   * One row of the Status Effects or Traits list, built in glossary.svelte from
   * each registered class. `instance` is a status effect instance, or a trait's
   * prototype, kept so the hooks it implements can be read off it.
   */
  type StatusEffectEntry = {
    title: string;
    text: string;
    icon: string;
    tags: Array<string>;
    instance?: Record<string, unknown>;
  };

  interface Props {
    data: StatusEffectEntry;
    /** The polarity filter selected in glossary.svelte, empty when off. */
    tags?: Array<string>;
  }

  let { data, tags = [] }: Props = $props();

  /**
   * Any of the selected tags is a match, not all of them. `every` meant that
   * turning on both Boon and Bane asked for an effect that was somehow
   * both, and the list came back empty.
   */
  let display = $derived(!tags.length || tags.some((tag) => data.tags?.includes(tag)));

  let polarity = $derived(
    data.tags?.includes(ModifierTags.Boon)
      ? ModifierTags.Boon
      : data.tags?.includes(ModifierTags.Bane)
        ? ModifierTags.Bane
        : '',
  );

  // boon/bane drive the polarity chip; anything else is shown as a plain tag.
  let otherTags = $derived(
    (data.tags ?? []).filter((tag) => tag !== ModifierTags.Boon && tag !== ModifierTags.Bane),
  );

  let timings = $derived(describeTiming(data.instance));
</script>

{#if display}
  <div class="container entry {polarity}">
    <div class="icon">
      <Icon name={data.icon} />
    </div>

    <div class="information">
      <div class="heading">
        <h2>{data.title}</h2>

        <div class="chips">
          {#if polarity}
            <span class="chip {polarity}">
              {polarity === ModifierTags.Boon ? 'Boon' : 'Bane'}
            </span>
          {/if}

          {#each otherTags as tag}
            <span class="chip">{tag}</span>
          {/each}
        </div>
      </div>

      <Text data={data.text} class="text" />

      {#if timings.length}
        <div class="timings">
          {#each timings as timing}
            <span class="timing">{timing}</span>
          {/each}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style lang="less">
  @import '../../../defines.less';

  .container {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-gap: 14px;
    align-items: start;

    text-align: left;

    // The frame round the icon carries the polarity, so the list can still be
    // read as two streams without going near the chips -- and it is the same
    // green and red the effects wear on a portrait in the battle screen.
    &.boon {
      --plate-accent: var(--green);
    }
    &.bane {
      --plate-accent: var(--red);
    }
  }

  .icon {
    .glossary-plate(56px);
    --icon-size: 32px;

    @media @mobile {
      .glossary-plate(44px);
      --icon-size: 26px;
    }
  }

  .information {
    display: flex;
    flex-direction: column;
    grid-gap: 6px;
    min-width: 0;
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    // Left-packed, not space-between: without a card edge to push against, a
    // right-aligned chip just floats off on its own halfway across the page.
    justify-content: flex-start;
    grid-gap: 8px;

    h2 {
      font-size: 1.15rem;
      text-transform: capitalize;
    }
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 4px;

    .chip {
      padding: 2px 9px;
      font-size: 0.6rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      opacity: 0.75;
      border: 1px solid currentColor;
      border-radius: 999px;

      &.boon {
        color: var(--green);
      }
      &.bane {
        color: var(--red);
      }
    }
  }

  .container :global(.text) {
    font-size: 0.9rem;
    opacity: 0.85;
  }

  .timings {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 6px;
    margin-top: 2px;

    .timing {
      padding: 3px 10px;
      font-size: 0.68rem;
      letter-spacing: 0.03em;
      opacity: 0.6;
      background: var(--background-colour-dark);
      border-radius: 6px;
    }
  }
</style>
